import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { ClipboardCheck, Plus, Search, Users, FileText, ArrowRight, Trash2, Building2, Upload, FileType2, Star } from "lucide-react";

const EMPTY_CUSTOMER = {
  ragione_sociale: "", partita_iva: "", codice_fiscale: "", ateco: "",
  pec: "", email: "", telefono: "",
  rappresentante_legale: "", datore_di_lavoro: "",
  indirizzo_sede: "", indirizzo_legale: "",
  rspp: "", rls: "", medico_competente: "",
  tipo_attivita: "",
  numero_lavoratori: "", numero_donne: "",
  numero_minorenni: "", numero_apprendisti: "", numero_stranieri: "",
  superficie_mq: "", piani: "",
};

export default function DVRDashboardPage() {
  const nav = useNavigate();
  const [summary, setSummary] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [dvrs, setDvrs] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [q, setQ] = useState("");
  const [customerOpen, setCustomerOpen] = useState(false);
  const [dvrOpen, setDvrOpen] = useState(false);
  const [tplOpen, setTplOpen] = useState(false);
  const [tplForm, setTplForm] = useState({ name: "", file: null });
  const [cForm, setCForm] = useState(EMPTY_CUSTOMER);
  const [editingCustomerId, setEditingCustomerId] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState("");
  // Import da aziende clienti già esistenti (collezione companies)
  const [companies, setCompanies] = useState([]);
  const [dvrSourceMode, setDvrSourceMode] = useState("existing"); // "existing" | "company"
  const [selectedCompany, setSelectedCompany] = useState("");
  const [companySearch, setCompanySearch] = useState("");
  const [importing, setImporting] = useState(false);

  const filteredCompanies = useMemo(() => {
    const q = companySearch.toLowerCase();
    const base = !companySearch ? companies : companies.filter((co) =>
      (co.name || "").toLowerCase().includes(q) ||
      (co.vat_number || "").toLowerCase().includes(q) ||
      (co.address || "").toLowerCase().includes(q)
    );
    return base.slice(0, 200);
  }, [companies, companySearch]);

  const load = () => {
    api.get("/dvr/summary").then((r) => setSummary(r.data)).catch(() => {});
    api.get("/dvr/customers").then((r) => setCustomers(r.data)).catch(() => {});
    api.get("/dvr/documents").then((r) => setDvrs(r.data)).catch(() => {});
    api.get("/dvr/templates").then((r) => setTemplates(r.data)).catch(() => {});
    api.get("/companies").then((r) => setCompanies(Array.isArray(r.data) ? r.data : (r.data?.items || []))).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const openNewCustomer = () => {
    setEditingCustomerId(null);
    setCForm(EMPTY_CUSTOMER);
    setCustomerOpen(true);
  };
  const openEditCustomer = (c) => {
    setEditingCustomerId(c.id);
    setCForm({
      ...EMPTY_CUSTOMER,
      ...c,
      // numeric fields: coerce back to string so <Input type=number> is happy
      numero_lavoratori: c.numero_lavoratori ?? "",
      numero_donne: c.numero_donne ?? "",
      numero_minorenni: c.numero_minorenni ?? "",
      numero_apprendisti: c.numero_apprendisti ?? "",
      numero_stranieri: c.numero_stranieri ?? "",
      superficie_mq: c.superficie_mq ?? "",
      piani: c.piani ?? "",
    });
    setCustomerOpen(true);
  };

  const submitCustomer = async (e) => {
    e.preventDefault();
    // Coerce numeric fields to numbers (or omit if empty).
    const payload = { ...cForm };
    for (const k of ["numero_lavoratori", "numero_donne", "numero_minorenni",
                     "numero_apprendisti", "numero_stranieri", "superficie_mq", "piani"]) {
      if (payload[k] === "" || payload[k] === null) delete payload[k];
      else payload[k] = Number(payload[k]);
    }
    try {
      if (editingCustomerId) {
        await api.put(`/dvr/customers/${editingCustomerId}`, payload);
        toast.success("Cliente aggiornato — modifiche propagate ai DVR attivi");
      } else {
        await api.post("/dvr/customers", payload);
        toast.success("Cliente DVR creato");
      }
      setCustomerOpen(false);
      setEditingCustomerId(null);
      setCForm(EMPTY_CUSTOMER);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
  };

  const startNewDVR = async (e) => {
    e.preventDefault();
    try {
      let customerId = selectedCustomer;
      if (dvrSourceMode === "company") {
        if (!selectedCompany) return toast.error("Seleziona un'azienda cliente");
        setImporting(true);
        const imp = await api.post("/dvr/customers/import-from-company", { company_id: selectedCompany });
        customerId = imp.data.id;
        setImporting(false);
      }
      if (!customerId) return toast.error("Seleziona un cliente");
      const r = await api.post("/dvr/documents", { customer_id: customerId });
      setDvrOpen(false);
      setDvrSourceMode("existing");
      setSelectedCompany("");
      setSelectedCustomer("");
      setCompanySearch("");
      nav(`/dvr/document/${r.data.id}`);
    } catch (err) {
      setImporting(false);
      toast.error(err.response?.data?.detail || "Errore");
    }
  };

  const deleteDvr = async (id) => {
    if (!window.confirm("Eliminare questo DVR?")) return;
    await api.delete(`/dvr/documents/${id}`);
    load();
  };

  const uploadTemplate = async (e) => {
    e.preventDefault();
    if (!tplForm.file || !tplForm.name.trim()) return toast.error("Nome e file .docx richiesti");
    if (!tplForm.file.name.toLowerCase().endsWith(".docx")) return toast.error("Il file deve essere .docx");
    const fd = new FormData(); fd.append("file", tplForm.file); fd.append("name", tplForm.name);
    try {
      await api.post("/dvr/templates", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Template caricato");
      setTplOpen(false); setTplForm({ name: "", file: null });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore upload"); }
  };
  const setDefaultTpl = async (id) => {
    await api.put(`/dvr/templates/${id}/default`);
    toast.success("Template predefinito impostato");
    load();
  };
  const deleteTpl = async (id) => {
    if (!window.confirm("Eliminare questo template?")) return;
    await api.delete(`/dvr/templates/${id}`);
    load();
  };

  const filteredCustomers = customers.filter((c) =>
    c.ragione_sociale.toLowerCase().includes(q.toLowerCase()) ||
    (c.partita_iva || "").includes(q)
  );
  const filteredDvrs = dvrs.filter((d) =>
    (d.customer_name || "").toLowerCase().includes(q.toLowerCase()) ||
    (d.title || "").toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div data-testid="dvr-dashboard" className="space-y-8">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
            <ClipboardCheck className="w-8 h-8 text-primary" /> Redazione DVR
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Sistema guidato per la valutazione dei rischi con AI · D.Lgs. 81/08
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Dialog open={tplOpen} onOpenChange={setTplOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="dvr-template-btn" className="rounded-full btn-lift">
                <FileType2 className="w-4 h-4 mr-1.5" /> Template ({templates.length})
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader><DialogTitle>Template DVR (.docx)</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Carica un tuo DVR .docx esistente da usare come base grafica. Verranno preservati intestazione, piè di pagina, copertina, stili, colori e margini. I contenuti saranno rigenerati dai dati del wizard.
                </p>
                <form onSubmit={uploadTemplate} className="flex gap-2 items-end">
                  <div className="flex-1">
                    <Label>Nome template</Label>
                    <Input value={tplForm.name} onChange={(e) => setTplForm({ ...tplForm, name: e.target.value })} placeholder="Es. Template Sicurgarda 2026" data-testid="dvr-tpl-name" />
                  </div>
                  <div>
                    <Label>File .docx</Label>
                    <input type="file" accept=".docx" onChange={(e) => setTplForm({ ...tplForm, file: e.target.files?.[0] || null })} className="text-xs" data-testid="dvr-tpl-file" />
                  </div>
                  <Button type="submit" className="rounded-full" data-testid="dvr-tpl-upload"><Upload className="w-4 h-4 mr-1.5" />Carica</Button>
                </form>
                <ul className="divide-y divide-border border border-border rounded-md max-h-72 overflow-y-auto">
                  {templates.length === 0 && <li className="p-4 text-sm text-muted-foreground">Nessun template. Caricane uno per personalizzare la grafica del DVR.</li>}
                  {templates.map((t) => (
                    <li key={t.id} className="p-3 flex items-center gap-3">
                      <FileType2 className="w-4 h-4 text-muted-foreground shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{t.name}</div>
                        <div className="text-xs text-muted-foreground truncate">{t.original_filename}</div>
                      </div>
                      {t.is_default && <span className="text-[10px] px-2 py-0.5 rounded bg-primary/10 text-primary font-semibold inline-flex items-center gap-1"><Star className="w-3 h-3" /> Predefinito</span>}
                      {!t.is_default && <button onClick={() => setDefaultTpl(t.id)} className="text-xs text-primary hover:underline" data-testid={`dvr-tpl-default-${t.id}`}>Imposta come predefinito</button>}
                      <button onClick={() => deleteTpl(t.id)} className="text-muted-foreground hover:text-destructive" data-testid={`dvr-tpl-delete-${t.id}`}><Trash2 className="w-4 h-4" /></button>
                    </li>
                  ))}
                </ul>
              </div>
            </DialogContent>
          </Dialog>
          <Dialog open={customerOpen} onOpenChange={(o) => { setCustomerOpen(o); if (!o) { setEditingCustomerId(null); setCForm(EMPTY_CUSTOMER); } }}>
            <DialogTrigger asChild>
              <Button variant="outline" onClick={openNewCustomer} data-testid="dvr-new-customer-btn" className="rounded-full btn-lift">
                <Users className="w-4 h-4 mr-1.5" /> Nuovo cliente DVR
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl">
              <DialogHeader>
                <DialogTitle>{editingCustomerId ? "Modifica cliente DVR" : "Nuovo cliente per DVR"}</DialogTitle>
              </DialogHeader>
              <form onSubmit={submitCustomer} className="grid grid-cols-2 gap-4 max-h-[75vh] overflow-y-auto pr-2" data-testid="dvr-customer-form">
                {!editingCustomerId && companies.length > 0 && (
                  <div className="col-span-2 border border-dashed border-border rounded-md p-3 bg-muted/20">
                    <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
                      Popola rapidamente da azienda cliente esistente
                    </div>
                    <select
                      className="w-full border border-border rounded-md px-3 py-2 text-sm bg-background"
                      onChange={(e) => {
                        const co = companies.find((x) => x.id === e.target.value);
                        if (!co) return;
                        setCForm((f) => ({
                          ...f,
                          ragione_sociale: co.name || f.ragione_sociale,
                          partita_iva: co.vat_number || f.partita_iva,
                          codice_fiscale: co.tax_code || f.codice_fiscale,
                          email: co.email || f.email,
                          telefono: co.phone || f.telefono,
                          indirizzo_sede: co.address || f.indirizzo_sede,
                          indirizzo_legale: co.address || f.indirizzo_legale,
                          rappresentante_legale: co.referent || f.rappresentante_legale,
                        }));
                        toast.success(`Dati importati da "${co.name}"`);
                      }}
                      defaultValue=""
                      data-testid="dvr-customer-import-picker"
                    >
                      <option value="">— seleziona un'azienda da cui importare i dati —</option>
                      {companies.map((co) => (
                        <option key={co.id} value={co.id}>
                          {co.name} {co.vat_number ? `· P.IVA ${co.vat_number}` : ""}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="col-span-2 border-b border-border pb-1 text-xs uppercase tracking-widest text-muted-foreground">Anagrafica</div>
                <div className="col-span-2"><Label>Ragione sociale *</Label><Input required value={cForm.ragione_sociale} onChange={(e) => setCForm({ ...cForm, ragione_sociale: e.target.value })} /></div>
                <div><Label>P. IVA</Label><Input value={cForm.partita_iva} onChange={(e) => setCForm({ ...cForm, partita_iva: e.target.value })} /></div>
                <div><Label>Codice fiscale</Label><Input value={cForm.codice_fiscale} onChange={(e) => setCForm({ ...cForm, codice_fiscale: e.target.value })} /></div>
                <div><Label>ATECO</Label><Input placeholder="es. 62.01.00" value={cForm.ateco} onChange={(e) => setCForm({ ...cForm, ateco: e.target.value })} /></div>
                <div><Label>Tipo attività</Label><Input placeholder="es. Officina meccanica, Ufficio, Cantiere…" value={cForm.tipo_attivita} onChange={(e) => setCForm({ ...cForm, tipo_attivita: e.target.value })} /></div>
                <div><Label>PEC</Label><Input type="email" value={cForm.pec} onChange={(e) => setCForm({ ...cForm, pec: e.target.value })} /></div>
                <div><Label>Email</Label><Input type="email" value={cForm.email} onChange={(e) => setCForm({ ...cForm, email: e.target.value })} /></div>
                <div><Label>Telefono</Label><Input value={cForm.telefono} onChange={(e) => setCForm({ ...cForm, telefono: e.target.value })} /></div>
                <div className="col-span-2"><Label>Indirizzo sede operativa</Label><Input value={cForm.indirizzo_sede} onChange={(e) => setCForm({ ...cForm, indirizzo_sede: e.target.value })} /></div>
                <div className="col-span-2"><Label>Indirizzo sede legale</Label><Input value={cForm.indirizzo_legale} onChange={(e) => setCForm({ ...cForm, indirizzo_legale: e.target.value })} /></div>

                <div className="col-span-2 border-b border-border pb-1 text-xs uppercase tracking-widest text-muted-foreground mt-2">Organigramma (verrà precompilato nei DVR)</div>
                <div><Label>Datore di lavoro</Label><Input value={cForm.datore_di_lavoro} onChange={(e) => setCForm({ ...cForm, datore_di_lavoro: e.target.value })} /></div>
                <div><Label>Rappresentante legale</Label><Input value={cForm.rappresentante_legale} onChange={(e) => setCForm({ ...cForm, rappresentante_legale: e.target.value })} /></div>
                <div><Label>RSPP</Label><Input value={cForm.rspp} onChange={(e) => setCForm({ ...cForm, rspp: e.target.value })} /></div>
                <div><Label>RLS</Label><Input value={cForm.rls} onChange={(e) => setCForm({ ...cForm, rls: e.target.value })} /></div>
                <div className="col-span-2"><Label>Medico competente</Label><Input value={cForm.medico_competente} onChange={(e) => setCForm({ ...cForm, medico_competente: e.target.value })} /></div>

                <div className="col-span-2 border-b border-border pb-1 text-xs uppercase tracking-widest text-muted-foreground mt-2">Contesto azienda</div>
                <div><Label>N° lavoratori</Label><Input type="number" min="0" value={cForm.numero_lavoratori} onChange={(e) => setCForm({ ...cForm, numero_lavoratori: e.target.value })} /></div>
                <div><Label>di cui donne</Label><Input type="number" min="0" value={cForm.numero_donne} onChange={(e) => setCForm({ ...cForm, numero_donne: e.target.value })} /></div>
                <div><Label>di cui minorenni</Label><Input type="number" min="0" value={cForm.numero_minorenni} onChange={(e) => setCForm({ ...cForm, numero_minorenni: e.target.value })} /></div>
                <div><Label>di cui apprendisti</Label><Input type="number" min="0" value={cForm.numero_apprendisti} onChange={(e) => setCForm({ ...cForm, numero_apprendisti: e.target.value })} /></div>
                <div><Label>di cui stranieri</Label><Input type="number" min="0" value={cForm.numero_stranieri} onChange={(e) => setCForm({ ...cForm, numero_stranieri: e.target.value })} /></div>
                <div><Label>Superficie (mq)</Label><Input type="number" min="0" value={cForm.superficie_mq} onChange={(e) => setCForm({ ...cForm, superficie_mq: e.target.value })} /></div>
                <div><Label>Piani</Label><Input type="number" min="0" value={cForm.piani} onChange={(e) => setCForm({ ...cForm, piani: e.target.value })} /></div>

                <DialogFooter className="col-span-2">
                  {editingCustomerId && (
                    <div className="text-xs text-muted-foreground mr-auto self-center">
                      Le modifiche verranno propagate automaticamente a tutti i DVR attivi (non firmati) di questo cliente.
                    </div>
                  )}
                  <Button type="submit" className="rounded-full btn-lift" data-testid="dvr-customer-submit">
                    {editingCustomerId ? "Aggiorna cliente" : "Salva cliente"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          <Dialog open={dvrOpen} onOpenChange={setDvrOpen}>
            <DialogTrigger asChild>
              <Button data-testid="dvr-new-btn" className="rounded-full btn-lift">
                <Plus className="w-4 h-4 mr-1.5" /> Nuovo DVR
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Avvia un nuovo DVR</DialogTitle></DialogHeader>
              <form onSubmit={startNewDVR} className="space-y-4">
                <div className="flex gap-2 border-b border-border">
                  <button
                    type="button"
                    onClick={() => setDvrSourceMode("existing")}
                    className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition ${dvrSourceMode === "existing" ? "border-slate-900 text-slate-900" : "border-transparent text-muted-foreground hover:text-foreground"}`}
                    data-testid="dvr-tab-existing"
                  >
                    Cliente DVR esistente
                  </button>
                  <button
                    type="button"
                    onClick={() => setDvrSourceMode("company")}
                    className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition ${dvrSourceMode === "company" ? "border-slate-900 text-slate-900" : "border-transparent text-muted-foreground hover:text-foreground"}`}
                    data-testid="dvr-tab-company"
                  >
                    Importa da azienda cliente ({companies.length})
                  </button>
                </div>

                {dvrSourceMode === "existing" ? (
                  <div>
                    <Label>Seleziona cliente DVR</Label>
                    <select
                      className="w-full border border-border rounded-md px-3 py-2 text-sm bg-background"
                      value={selectedCustomer}
                      onChange={(e) => setSelectedCustomer(e.target.value)}
                      data-testid="dvr-select-customer"
                    >
                      <option value="">—</option>
                      {customers.map((c) => (
                        <option key={c.id} value={c.id}>{c.ragione_sociale}</option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Label>Cerca azienda cliente</Label>
                    <Input
                      value={companySearch}
                      onChange={(e) => setCompanySearch(e.target.value)}
                      placeholder="Filtra per nome, P.IVA…"
                      data-testid="dvr-company-search"
                    />
                    <div className="max-h-56 overflow-y-auto border border-border rounded-md divide-y divide-border">
                      {filteredCompanies
                        .map((co) => (
                          <label
                            key={co.id}
                            className={`flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-muted/40 ${selectedCompany === co.id ? "bg-primary/5" : ""}`}
                            data-testid={`dvr-company-row-${co.id}`}
                          >
                            <input
                              type="radio"
                              name="company"
                              value={co.id}
                              checked={selectedCompany === co.id}
                              onChange={() => setSelectedCompany(co.id)}
                            />
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium truncate">{co.name}</div>
                              <div className="text-xs text-muted-foreground truncate">
                                P.IVA {co.vat_number || "—"} · {co.address || "—"}
                              </div>
                            </div>
                          </label>
                        ))}
                      {companies.length === 0 && (
                        <div className="px-3 py-4 text-sm text-muted-foreground">
                          Nessuna azienda cliente presente. Aggiungine dalla sezione "Aziende".
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      L'anagrafica sarà importata (ragione sociale, P.IVA, indirizzo, referente…) creando un nuovo cliente DVR — potrai poi completare con dati specifici (RSPP, ATECO, mansioni…).
                    </p>
                  </div>
                )}

                <DialogFooter>
                  <Button
                    type="submit"
                    className="rounded-full"
                    disabled={importing || (dvrSourceMode === "existing" ? !selectedCustomer : !selectedCompany)}
                    data-testid="dvr-start-submit"
                  >
                    {importing ? "Importazione…" : "Inizia DVR"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Clienti" value={summary?.customers ?? "…"} icon={Building2} accent="bg-emerald-100 text-emerald-700" />
        <StatCard label="DVR totali" value={summary?.dvrs_total ?? "…"} icon={FileText} accent="bg-blue-100 text-blue-700" />
        <StatCard label="In lavorazione" value={summary?.in_progress ?? "…"} icon={ClipboardCheck} accent="bg-amber-100 text-amber-700" />
        <StatCard label="Ultimo update" value={summary?.recent?.[0]?.updated_at?.slice(0, 10) || "—"} icon={ArrowRight} accent="bg-slate-100 text-slate-700" />
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca cliente o DVR…" className="pl-9 h-10" data-testid="dvr-search" />
      </div>

      {/* DVR list */}
      <section className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="px-5 py-3 border-b border-border font-semibold">DVR recenti</div>
        <ul className="divide-y divide-border">
          {filteredDvrs.length === 0 && <li className="px-5 py-6 text-sm text-muted-foreground">Nessun DVR ancora. Crea il primo cliente e avvia un DVR.</li>}
          {filteredDvrs.map((d) => (
            <li key={d.id} className="px-5 py-3 flex items-center gap-4">
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{d.title}</div>
                <div className="text-xs text-muted-foreground">{d.customer_name} · {d.status} · agg. {d.updated_at?.slice(0, 10)}</div>
              </div>
              <div className="w-32 bg-muted rounded-full h-2 overflow-hidden">
                <div className="h-full bg-primary" style={{ width: `${d.progress_pct || 0}%` }} />
              </div>
              <span className="text-xs text-muted-foreground w-10">{d.progress_pct || 0}%</span>
              <Button size="sm" variant="outline" className="rounded-full" onClick={() => nav(`/dvr/document/${d.id}`)} data-testid={`dvr-open-${d.id}`}>
                Apri <ArrowRight className="w-3.5 h-3.5 ml-1" />
              </Button>
              <button onClick={() => deleteDvr(d.id)} className="text-muted-foreground hover:text-destructive p-1" data-testid={`dvr-delete-${d.id}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      </section>

      {/* Customers list */}
      <section className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="px-5 py-3 border-b border-border font-semibold">Clienti DVR ({filteredCustomers.length})</div>
        <ul className="divide-y divide-border max-h-96 overflow-y-auto">
          {filteredCustomers.length === 0 && <li className="px-5 py-6 text-sm text-muted-foreground">Nessun cliente.</li>}
          {filteredCustomers.map((c) => (
            <li key={c.id} className="px-5 py-3 flex items-center gap-4">
              <Building2 className="w-4 h-4 text-muted-foreground shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{c.ragione_sociale}</div>
                <div className="text-xs text-muted-foreground">P.IVA {c.partita_iva || "—"} · ATECO {c.ateco || "—"} · {c.indirizzo_sede || "—"}</div>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="rounded-full"
                onClick={() => openEditCustomer(c)}
                data-testid={`dvr-customer-edit-${c.id}`}
              >
                Modifica
              </Button>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, accent }) {
  return (
    <div className="bg-card border border-border rounded-lg p-5 card-hover">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground">{label}</div>
          <div className="mt-3 text-3xl font-bold tracking-tight">{value}</div>
        </div>
        <div className={`w-10 h-10 grid place-items-center rounded-md ${accent}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
}
